import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  message,
  Col,
  Divider,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  TagProps,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleFilled,
  DownloadOutlined,
  DownOutlined,
  ExportOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  UpOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import {
  CampaignDetails,
  CampaignInfo,
  CompletedCampaignViewModel,
} from "../models/campaign";
import { useBrandStore } from "../store/brandStore";
import { campaignService } from "../services/campaignService";
import { billingService } from "../services/billingService";
import {
  CampaignTypeEnum,
  ChannelEnum,
  EndScheduleEnum,
  ModeEnum,
  StatusEnum,
  TemplatesTypeEnum,
  TypeEnum,
  WeekDay,
} from "../constants/enums";
import {
  CampaignTypeLabel,
  ChannelKeyMap,
  getTemplateLabel,
  prettifyDynamicSignupFormKey,
  rfmPeriodToLabel,
  StatusKeyMap,
  TEMPLATE_TYPE_DESCRIPTIONS,
} from "../constants/labels";
import DOMPurify from "dompurify";
import { RoutePaths } from "../routes/route";
import { useQueryNavigator } from "../utils/navigate";
import CampaignAlertModal from "../components/common/CampaignAlertModal";
import { Variant } from "../constants/type";
import { Image as AntdImage } from "antd";
import { Voucher } from "../models/voucher";
import { EVoucher } from "../models/evoucher";
import { CardData } from "../models/campaignPerformances";
import CampaignMemberLogTable from "../components/common/CampaignMemberLogTable";
import ResponsivePagination from "../components/common/ResponsivePagination";
import RFMGeneratingModal from "../components/common/GeneratingDataModal";
import CustomModal from "../components/common/Modal";
import {
  formatMessageContent,
  getCampaignEVoucherSkus,
  mapToEVouchers,
} from "../utils/convert";
import { getChannelCost } from "../constants/cost";
import { getSmsSegments, isGsmText } from "../constants/text";
import { MessageTemplateData } from "../models/messageTemplates";
import PreviewMemberModal from "../components/common/PreviewMemberModal";
import ExcludeMemberPreviewModal from "../components/common/ExcludeMemberPreviewModal";
import { formatCurrency } from "../utils/format";
import TargetMemberPreviewModal from "../components/common/TargetMemberPreviewModal";
import {
  TARGET_MEMBER_FOUND,
  TARGET_MEMBER_NOT_FOUND,
} from "../utils/targetMember";

const currencySGD = (v: number) => `SGD ${v.toFixed(2)}`;

type OverviewRow = {
  key: string;
  field: React.ReactNode;
  value: React.ReactNode;
};

const columns: ColumnsType<OverviewRow> = [
  { title: null, dataIndex: "field", key: "field", width: "25%" },
  { title: null, dataIndex: "value", key: "value" },
];

const DetailCampaign: React.FC = () => {
  const initialCardData: CardData[] = [
    {
      key: "card-data-1",
      title: "Revenue via Rewards",
      value: "$0",
      icon: "/icon/icon-sack-dollar.svg",
      order: 0,
      tooltip: (
        <>
          Total revenue generated from customers who received and
          <br />
          responded to this campaign (generated with voucher or point usage)
        </>
      ),
    },
    {
      key: "card-data-2",
      title: "Revenue from Campaign",
      value: "$0",
      order: 0,

      icon: "/icon/icon-revenue-campaign.svg",
      tooltip: (
        <>
          Total revenue generated from campaign activities based on targeted
          members spending
        </>
      ),
    },
    {
      key: "card-data-3",
      title: "Return of Investment (ROI)",
      value: "0%",
      icon: "/icon/icon-roi.svg",
      tooltip: (
        <>
          Return on Investment = (Revenue – Cost) / Cost.
          <br />
          Based on the total campaign cost and revenue.
        </>
      ),
    },
    {
      key: "card-data-4",
      title: "Click Through Rate (CTR)",
      value: "0%",
      order: 0,
      countLabel: "Clicks",
      icon: "/icon/icon-ctr.svg",
      tooltip: (
        <>
          Click-through rate = (Number of clicks / Messages delivered)
          <br />× 100%
        </>
      ),
    },
  ];
  const [cardData, setCardData] = useState<CardData[]>(initialCardData);
  const [loading, setLoading] = useState<boolean>(false);
  const [messageTimeoutModal, setMessageTimeoutModal] = useState(
    "This campaign is approaching its scheduled send time, so editing is no longer available",
  );
  const isMobile = window.innerWidth < 768;

  const [titleTimeoutModal, setTitleTimeoutModal] = useState(
    "Editing Unavailable",
  );

  const [data, setData] = useState<CampaignDetails | null>(null);
  const isProfileCompletionReminder =
    data?.Type === CampaignTypeEnum.ProfileCompletionReminder;
  const { brandId } = useBrandStore();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const id = query.get("id");
  const navigateWithQuery = useQueryNavigator();
  const [variant, setVariant] = useState<Variant | null>(null);
  const { Title, Text } = Typography;
  const [actionType, setActionType] = useState<string | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [selectedMessageTemplates, setSelectedMessageTemplates] =
    useState<MessageTemplateData | null>(null);
  const [loadingEVoucher, setLoadingEVoucher] = useState(false);
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);

  const [selectedEVouchers, setSelectedEVouchers] = useState<EVoucher[]>([]);
  const [listEVoucher, setListEVoucher] = useState<EVoucher[]>([]);
  // Collapsed by default when showing 3+ selected bundles — "Show N more"
  // reveals the rest inline instead of always listing every bundle.
  const [evoucherListExpanded, setEvoucherListExpanded] = useState(false);

  const [
    isModalSuccessDeleteCampaignVisible,
    setIsModalSuccessDeleteCampaignVisible,
  ] = useState(false);
  const [
    isModalSuccessActivateCampaignVisible,
    setIsModalSuccessActivateCampaignVisible,
  ] = useState(false);
  const [, setPrice] = useState(0);

  // Billing flow
  const [, setBillingModalOpen] = useState(false);
  const [billingContext] = useState<"activate" | "pay">("activate");
  const [paymentSuccessModalOpen, setPaymentSuccessModalOpen] = useState(false);
  const [paymentFailedModalOpen, setPaymentFailedModalOpen] = useState(false);

  const [allRunLogPerformances, setAllRunLogPerformances] = useState<
    CompletedCampaignViewModel[]
  >([]);

  const [selectedRunLogId, setSelectedRunLogId] = useState<string>("all");
  const [runLogPage, setRunLogPage] = useState(1);
  const [runLogPageSize, setRunLogPageSize] = useState(5);
  const [downloadingRunLogId, setDownloadingRunLogId] = useState<number | null>(
    null,
  );

  const handleDownloadDeliveryLog = async (campaignRunLogId?: number) => {
    const safeRunLogId = Number.isFinite(campaignRunLogId)
      ? campaignRunLogId
      : undefined;
    setDownloadingRunLogId(safeRunLogId ?? -1);
    try {
      const blobData = await campaignService.downloadRecipient(
        data?.Id ?? 0,
        safeRunLogId,
      );
      if (!blobData) {
        message.error("Failed to download. Please try again.");
        return;
      }
      const blob = new Blob([blobData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = data?.Name
        ? data.Name.trim().replace(/\s+/g, "_")
        : "Recipient";
      const runIndex =
        safeRunLogId != null
          ? allStartedRunLogs.findIndex((log) => log.Id === safeRunLogId)
          : -1;
      const suffix = runIndex >= 0 ? `_Run${runIndex + 1}` : "_AllRuns";
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Recipient_${fileName}${suffix}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download error:", error);
      message.error("An error occurred while generating Excel.");
    } finally {
      setDownloadingRunLogId(null);
    }
  };

  const allStartedRunLogs = useMemo(
    () =>
      (data?.CampaignRunLog ?? []).filter(
        (log) => log.Status === true || log.Status === null,
      ),
    [data],
  );

  const hasActuallyStarted = useMemo(
    () =>
      (data?.CampaignRunLog ?? []).some(
        (log) => log.StartDate && new Date(log.StartDate) <= new Date(),
      ),
    [data],
  );

  const displayCardData = useMemo(() => {
    if (selectedRunLogId === "all" || data?.ScheduleType === TypeEnum.Onetime) {
      return cardData;
    }

    const perf = allRunLogPerformances.find(
      (p) => String(p.CampaignRunLogId) === selectedRunLogId,
    );
    if (!perf) return cardData;

    return cardData.map((card) => {
      if (card.key === "card-data-1")
        return {
          ...card,
          value: formatCurrency(perf.RevenueRewards),
          order: perf.TotalOrderRewards,
        };
      if (card.key === "card-data-2")
        return {
          ...card,
          value: formatCurrency(perf.RevenueCampaign),
          order: perf.TotalOrderCampaign,
        };
      if (card.key === "card-data-3")
        return { ...card, value: `${perf.ROI.toLocaleString("en-US")}%` };
      if (card.key === "card-data-4")
        return {
          ...card,
          value: `${perf.CTR.toLocaleString("en-US")}%`,
          order: perf.Clicks,
        };
      return card;
    });
  }, [selectedRunLogId, cardData, allRunLogPerformances, data?.ScheduleType]);

  const STATUS_COLOR: Record<StatusEnum, TagProps["color"]> = {
    [StatusEnum.Draft]: undefined,
    [StatusEnum.Active]: "green",
    [StatusEnum.Paused]: "orange",
    [StatusEnum.Completed]: "blue",
    [StatusEnum.Cancelled]: "red",
  };
  const start = data?.CampaignStart ? new Date(data.CampaignStart) : null;

  // Derive billing status from the latest run log
  const isBilled =
    (data?.CampaignRunLog ?? [])
      .slice()
      .sort(
        (a, b) =>
          new Date(b.CreatedDate ?? 0).getTime() -
          new Date(a.CreatedDate ?? 0).getTime(),
      )[0]?.IsBilled ?? false;

  const isWithinOneHour = (() => {
    if (!start) return false;

    const now = new Date();
    const diffInHours = (start.getTime() - now.getTime()) / (1000 * 60 * 60);

    return diffInHours <= 1;
  })();

  const isRunning =
    data?.Status === StatusEnum.Active && start !== null && start <= new Date();

  const onClickAction = (variant: Variant) => {
    if (!start) return false;

    const now = new Date();
    const diffInHours = (start.getTime() - now.getTime()) / (1000 * 60 * 60);
    if (
      diffInHours <= 1 &&
      ((data?.Status === StatusEnum.Draft && variant === "activate") ||
        (data?.Status === StatusEnum.Active && variant === "cancel"))
    ) {
      if (variant === "activate") {
        setTitleTimeoutModal("Activation Unavailable");
        setMessageTimeoutModal(
          "The campaign is currently locked due to its upcoming send time. Please Update the campaign details before activating it.",
        );
      }
      if (variant === "cancel") {
        setTitleTimeoutModal("Cancelling Unavailable");
        setMessageTimeoutModal(
          "The campaign is locked as it gets closer to the scheduled send time.",
        );
      }
      setShowTimeout(true);
    } else {
      setVariant(variant);
    }
  };

  const [showTimeout, setShowTimeout] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isExcludePreviewOpen, setIsExcludePreviewOpen] = useState(false);
  const [excludeRows, setExcludeRows] = useState<Record<string, any>[]>([]);
  const [isTargetPreviewOpen, setIsTargetPreviewOpen] = useState(false);
  const [targetRows, setTargetRows] = useState<Record<string, any>[]>([]);
  const targetFoundCount = useMemo(
    () => targetRows.filter((r) => r.Status === TARGET_MEMBER_FOUND).length,
    [targetRows],
  );

  const openPreview = () => setIsPreviewOpen(true);
  const closePreview = () => setIsPreviewOpen(false);
  const isUnicodeText = !isGsmText(data?.Messages[0].Content ?? "");
  const smsSegments = getSmsSegments(
    data?.Messages[0].Content ?? "",
    isUnicodeText,
  );

  const maxReach = useMemo(() => {
    const budgetFinal = data?.Budget ?? 0;
    const cost = getChannelCost(data?.Channel ?? null);
    if (data?.Channel === ChannelEnum.Sms) {
      const realCostPerMember = cost * smsSegments;
      return realCostPerMember > 0
        ? Math.floor(budgetFinal / realCostPerMember)
        : 0;
    }

    return cost > 0 ? Math.floor(budgetFinal / cost) : 0;
  }, [smsSegments, data]);

  const overviewRows: OverviewRow[] = [
    {
      key: "created",
      field: "Created At",
      value: dayjs(data?.CreatedDate)
        .add(-7, "hour")
        .format("YYYY-MM-DD HH:mm"),
    },
    { key: "name", field: "Campaign Name", value: data?.Name },
    {
      key: "campaignType",
      field: "Campaign Type",
      value: data?.Type ? CampaignTypeLabel[data?.Type] : "-",
    },
    ...(!isProfileCompletionReminder
      ? [
          {
            key: "templateType",
            field: "Campaign Template Type",
            value: data?.TemplateType ? (
              <div>
                {getTemplateLabel(data.TemplateType)}
                <br />
                <span style={{ fontSize: 12, color: "#888" }}>
                  (
                  {TEMPLATE_TYPE_DESCRIPTIONS[
                    data.TemplateType as TemplatesTypeEnum
                  ]?.replace(
                    "{inactiveDays}",
                    String(data?.InactiveDays ?? 60),
                  )}
                  )
                </span>
              </div>
            ) : (
              "-"
            ),
          },
        ]
      : []),
    ...(data?.TemplateType === TemplatesTypeEnum.Default &&
    !isProfileCompletionReminder
      ? [
          {
            key: "inactiveDays",
            field: "Inactive Period",
            value: data?.InactiveDays ? data?.InactiveDays + " Days" : "-",
          },
        ]
      : []),
    ...(isProfileCompletionReminder
      ? [
          {
            key: "incompleteProfileFields",
            field: "Incomplete Profile Fields",
            value: (
              <Space size={[6, 6]} wrap>
                {data?.IncompleteProfileFields &&
                data.IncompleteProfileFields.length > 0
                  ? data.IncompleteProfileFields.map((f) => (
                      <Tag key={f}>{prettifyDynamicSignupFormKey(f)}</Tag>
                    ))
                  : "-"}
              </Space>
            ),
          },
        ]
      : []),
    {
      key: "Payment Status",
      field: "Payment Status",
      value: (
        <Space size={[6, 6]} wrap>
          {isBilled != null ? (
            <Tag color={isBilled ? "green" : "red"}>
              {isBilled ? "Paid" : "Unpaid"}
            </Tag>
          ) : (
            "-"
          )}
        </Space>
      ),
    },
    {
      key: "status",
      field: "Status",
      value: (
        <Space size={[6, 6]} wrap>
          {data?.Status != null ? (
            <Tag color={STATUS_COLOR[data.Status as StatusEnum]}>
              {StatusKeyMap[data.Status as StatusEnum]}
            </Tag>
          ) : (
            "-"
          )}
        </Space>
      ),
    },
    {
      key: "channel",
      field: "Channel",
      value: data?.Channel ? ChannelKeyMap[data?.Channel] : "-",
    },
    ...(!isProfileCompletionReminder
      ? [
          {
            key: "period",
            field: "Selected RFM Period",
            value:
              data?.RFMPeriod != null
                ? (rfmPeriodToLabel[data.RFMPeriod] ?? "-")
                : "-",
          },
          {
            key: "segments",
            field: "Target RFM Segment",
            value: (
              <Space size={[6, 6]} wrap>
                {data?.Segments && data.Segments.length > 0
                  ? data.Segments.map((s) => s.SegmentName).join(", ")
                  : "-"}
              </Space>
            ),
          },
          {
            key: "demographic",
            field: "Target Demographics (age)",
            value: (
              <Space size={[6, 6]} wrap>
                {data?.FromAge != null && data?.ToAge != null
                  ? `${data.FromAge} - ${data.ToAge}`
                  : "-"}
              </Space>
            ),
          },
          {
            key: "geographic",
            field: "Target Geographic (Postal Sector)",
            value: (
              <Space size={[6, 6]} wrap>
                {data?.PostalDistricts && data.PostalDistricts.length > 0
                  ? data.PostalDistricts.map((d) =>
                      d.PostalSectors && d.PostalSectors.length > 0 ? (
                        <Tag key={`District${d.DistrictId}`}>
                          {d.PostalSectors.join(", ")}
                        </Tag>
                      ) : null,
                    )
                  : "-"}
              </Space>
            ),
          },
          {
            key: "profile",
            field: "RFM Criteria Profile",
            value: data?.RFMProfileName,
          },
          {
            key: "PurchaseBrand",
            field: "Purchase Brand",
            value: (
              <Space size={[6, 6]} wrap>
                {data?.PurchaseBrandList && data.PurchaseBrandList.length > 0
                  ? data.PurchaseBrandList.map((d) => (
                      <Tag key={`${d}`}>{d}</Tag>
                    ))
                  : "All Brands"}
              </Space>
            ),
          },
        ]
      : []),
    ...(excludeRows.length > 0
      ? [
          {
            key: "excludeMembers",
            field: "Exclude Targeted Members (Optional)",
            value: (
              <Space size={12} wrap>
                <span>
                  <Text strong>{excludeRows.length} Excluded Members</Text>{" "}
                  <Text type="secondary">
                    (These members will be excluded from this campaign.)
                  </Text>
                </span>
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  style={{ padding: 0 }}
                  onClick={() => setIsExcludePreviewOpen(true)}>
                  Preview Excluded
                </Button>
              </Space>
            ),
          },
        ]
      : []),
    ...(targetRows.length > 0
      ? [
          {
            key: "targetMembers",
            field: "Target Member List",
            value: (
              <Row>
                <Col xs={24} md={12} xl={12}>
                  <Text strong>{targetFoundCount} Target Members Found</Text>{" "}
                  <Text type="secondary">
                    {targetFoundCount === targetRows.length
                      ? "(These members are selected for this campaign.)"
                      : `(Matched ${targetFoundCount} of ${targetRows.length} uploaded records.)`}
                  </Text>
                </Col>
                <Col
                  xs={24}
                  md={12}
                  xl={12}
                  style={{ textAlign: isMobile ? "left" : "right" }}>
                  <span
                    onClick={() => setIsTargetPreviewOpen(true)}
                    style={{
                      color: "#1677ff",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontWeight: 500,
                    }}>
                    Preview Members <ExportOutlined />
                  </span>
                </Col>
              </Row>
            ),
          },
        ]
      : []),
    {
      key: "reach",
      field: "Estimated Total Members",
      value: (
        <Row>
          <Col span={24}> {data?.EstimatedTotalMembers} Members</Col>
        </Row>
      ),
    },
    {
      key: "cpm",
      field: "Channel Cost per Message",
      value: currencySGD(getChannelCost(data?.Channel ?? null)),
    },
    {
      key: "budget",
      field: " Estimated Budget",
      value: currencySGD(
        getChannelCost(data?.Channel ?? null) *
          (data?.EstimatedTotalMembers ?? 0) *
          (data?.Channel === ChannelEnum.Sms ? smsSegments : 1),
      ),
    },
    {
      key: "budget1",
      field:
        data?.ScheduleType !== TypeEnum.Onetime
          ? "Budget per Recurring Run"
          : "Budget Amount Entire Campaign (Allocated)",
      value: currencySGD(data?.Budget ?? 0),
    },
    ...(data?.Status === StatusEnum.Completed
      ? [
          {
            key: "finalBudget",
            field: "Final Budget (Actual Spend)",
            value: currencySGD(
              data?.CampaignRunLog?.[0]?.MemberLogs
                ? data.CampaignRunLog[0].MemberLogs.map(
                    (log) => (log.Fee || 0) * (log.Count || 0),
                  ).reduce((sum, val) => sum + val, 0)
                : 0,
            ),
          },
        ]
      : []),
    {
      key: "reach1",
      field: "Maximum Reach",
      value: (
        // `${maxReach} Members`
        <Row>
          <Col xs={24} md={12} xl={12}>
            {(() => {
              const est = data?.EstimatedTotalMembers ?? 0;
              const max = maxReach;

              if (est === max) {
                return `${max} Members`;
              }

              if (max > est) {
                return `${est} of ${max} Members`;
              }

              return `${max} of ${est} Members based on estimation budget`;
            })()}
          </Col>

          <Col
            xs={24}
            md={12}
            xl={12}
            style={{ textAlign: isMobile ? "left" : "right" }}>
            {data?.Status === StatusEnum.Active &&
              data?.ScheduleType === TypeEnum.Onetime && (
                <span
                  onClick={openPreview}
                  style={{
                    color: "#1677ff",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontWeight: 500,
                  }}>
                  Preview Members <ExportOutlined />
                </span>
              )}
          </Col>
        </Row>
      ),
    },
    ...(data?.Status === StatusEnum.Completed || isRunning
      ? [
          {
            key: "totalMembers",
            field: "Final Reach Members",
            value: (
              <>
                {data?.CampaignRunLog?.[0]?.TotalMembers + " Members"}
                <b>
                  {" (" +
                    (data?.CampaignRunLog?.[0]?.SuccessCount ?? 0) +
                    " Success | " +
                    (data?.CampaignRunLog?.[0]?.FailCount ?? 0) +
                    " Failed)"}
                </b>
              </>
            ),
          },
        ]
      : []),
  ];

  const scheduleRows: OverviewRow[] = [
    {
      key: "stype",
      field: "Schedule Campaign",
      value:
        data?.ScheduleType === 1 ? "One-Time Campaign" : "Recurring Campaign",
    },
    {
      key: "sendat",
      field: "Send Date & Time",
      value: (() => {
        const start = dayjs(data?.CampaignStart).format("YYYY-MM-DD HH:mm");
        if (data?.Status === StatusEnum.Cancelled) {
          return `${start}`;
        }

        if (data?.ScheduleType === TypeEnum.Onetime && data?.CampaignStart) {
          const end = data.CampaignEnd
            ? dayjs(data.CampaignEnd).format("YYYY-MM-DD HH:mm")
            : null;

          if (data.Status === StatusEnum.Completed) {
            return start;
          }

          return end ? `${start} (Estimated to Complete by ${end})` : start;
        }

        if (data?.ScheduleType !== TypeEnum.Onetime) {
          return start;
        }

        return "-";
      })(),
    },
    ...(data?.ScheduleType !== TypeEnum.Onetime
      ? [
          {
            key: "repeatEvery",
            field: "Repeat every",
            value:
              data?.ScheduleType === TypeEnum.Recurring_Monthly ? (
                <div>Every {data?.RepeatMonth} month(s)</div>
              ) : (
                <div>
                  Every{" "}
                  {(data?.RepeatDaysList ?? [])
                    .slice()
                    .sort((a, b) => a - b)
                    .map((day) => WeekDay[day as WeekDay])
                    .join(", ")}
                </div>
              ),
          },
        ]
      : []),
    ...(data?.ScheduleType !== TypeEnum.Onetime
      ? [
          {
            key: "ScheduleEndType",
            field: "End Schedule",
            value:
              data?.ScheduleEndType === EndScheduleEnum.AfterOccurrences ? (
                <div>After {data?.MaxOccurrences} Occurrences</div>
              ) : (
                <div>On {dayjs(data?.EndDate).format("YYYY-MM-DD HH:mm")}</div>
              ),
          },
        ]
      : []),
    ...(data?.ScheduleType === TypeEnum.Onetime
      ? [
          {
            key: "mode",
            field: "Target Members Mode",
            value: data?.Mode ? (
              <div>
                {data.Mode === ModeEnum.Refresh
                  ? "Refresh target before sending"
                  : "Use fixed target"}
                <br />
                <span style={{ fontSize: 12, color: "#888" }}>
                  {data.Mode === ModeEnum.Refresh
                    ? "(Target members will be refreshed 1 day before send time)."
                    : "(Target members remains the same as when the campaign was created)."}
                </span>
              </div>
            ) : (
              "-"
            ),
          },
        ]
      : []),
    ...(data?.ScheduleType !== TypeEnum.Onetime
      ? [
          {
            key: "retargeting",
            field: "Audience Re-targeting",
            value: data?.Mode ? (
              <div>
                {data.Retargeting === true
                  ? "Allow repeated targeting"
                  : "Prevent repeated targeting"}
                <br />
                <span style={{ fontSize: 12, color: "#888" }}>
                  {data.Retargeting === true
                    ? "(Member can receive this recurring campaign multiple times)."
                    : data.Type === CampaignTypeEnum.Voucher ||
                        data.Type === CampaignTypeEnum.VoucherPoint
                      ? "(Member will only be targeted again after previous voucher expiry)."
                      : "(Each member can only receive this campaign once)."}
                </span>
              </div>
            ) : (
              "-"
            ),
          },
        ]
      : []),
    {
      key: "extensionDays",
      field: "Extension Period",
      value: data?.ExtensionDays != null ? `${data.ExtensionDays} Days` : "-",
    },
    ...(data?.Status === StatusEnum.Completed
      ? [
          {
            key: "completedOn",
            field: "Completed On",
            value: data?.CampaignRunLog?.[0]?.EndDate
              ? dayjs(data.CampaignRunLog[0].EndDate)
                  .add(-7, "hour")
                  .format("YYYY-MM-DD HH:mm")
              : "-",
          },
        ]
      : []),
  ];

  const formatContent = (text: string, channel: ChannelEnum) =>
    formatMessageContent(text, channel);
  const previewFieldLabel =
    data?.Channel === ChannelEnum.Sms
      ? `Preview Message (${smsSegments} Message)`
      : "Preview Message";

  // Campaign has e-voucher SKU(s) saved, but none matched the fetched
  // e-voucher list — either the SKU isn't in the list, or the list itself
  // failed/came back empty. Falls back to showing just the selected count
  // instead of "-".
  const eVoucherNotFoundCount = useMemo(() => {
    if (selectedEVouchers.length > 0) return 0;
    return getCampaignEVoucherSkus(data).length;
  }, [data, selectedEVouchers]);

  const messageRows: OverviewRow[] = useMemo(() => {
    return [
      ...(data?.Type === CampaignTypeEnum.Voucher ||
      data?.Type === CampaignTypeEnum.VoucherPoint
        ? [
            {
              key: "reward",
              field: "Reward Included",
              value: `${selectedVoucher?.Name ?? "-"} | ${
                selectedVoucher?.DiscountLabel ?? "-"
              }`,
            },
          ]
        : []),
      ...(data?.Type === CampaignTypeEnum.EVoucher
        ? [
            {
              key: "reward",
              field: "Reward Included",
              value: loadingEVoucher ? (
                <Space>
                  <Spin size="small" />
                  Loading E-Voucher...
                </Space>
              ) : selectedEVouchers.length > 0 ? (
                selectedEVouchers.length === 1 ? (
                  `${selectedEVouchers[0].Name} | $${selectedEVouchers[0].Price}`
                ) : (
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: "100%" }}>
                    {(evoucherListExpanded
                      ? selectedEVouchers
                      : selectedEVouchers.slice(0, 2)
                    ).map((v) => (
                      <div key={v.Id}>
                        • {v.Name} | ${v.Price}
                      </div>
                    ))}
                    {selectedEVouchers.length > 2 && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => setEvoucherListExpanded((prev) => !prev)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            setEvoucherListExpanded((prev) => !prev);
                          }
                        }}
                        style={{ fontWeight: 600, cursor: "pointer" }}>
                        {evoucherListExpanded ? (
                          <>
                            Show Less <UpOutlined />
                          </>
                        ) : (
                          <>
                            Show {selectedEVouchers.length - 2} more{" "}
                            <DownOutlined />
                          </>
                        )}
                      </span>
                    )}
                  </Space>
                )
              ) : eVoucherNotFoundCount > 0 ? (
                `${eVoucherNotFoundCount} E-Vouchers Selected`
              ) : (
                "-"
              ),
            },

            {
              key: "ctaType",
              field: data?.EnableCustomTrackingLink
                ? "Customize CTA"
                : "Use default CTA",
              value: data?.EnableCustomTrackingLink
                ? "Customize the CTA label and destination link."
                : "Use the default CTA label and destination link.",
            },

            ...(data?.EnableCustomTrackingLink
              ? [
                  {
                    key: "ctaText",
                    field: "CTA Text",
                    value: data?.Messages?.[0]?.CustomCopyText ?? "-",
                  },
                  {
                    key: "ctaDestination",
                    field: "CTA Destination Link",
                    value: data?.Messages?.[0]?.CustomUrl ?? "-",
                  },
                ]
              : []),
          ]
        : []),
      ...(data?.Channel === ChannelEnum.WhatsAppPremium
        ? [
            {
              key: "message_template",
              field: "Message Template",
              value: selectedMessageTemplates?.Name ?? "-",
            },
          ]
        : []),
      ...(data?.Channel === ChannelEnum.Email
        ? [
            {
              key: "MessageSubject",
              field: "Email Subject",
              value: `${data?.Messages?.[0]?.MessageSubject ?? "-"}`,
            },
          ]
        : []),
      ...(data?.Channel === ChannelEnum.Email
        ? [
            {
              key: "MessagePreheader",
              field: "Preheader",
              value: `${data?.Messages?.[0]?.MessagePreheader ?? "-"}`,
            },
          ]
        : []),
      {
        key: "preview",
        field: previewFieldLabel,
        value: (
          <>
            {data?.Channel !== ChannelEnum.Email && data?.ImageUrl && (
              <AntdImage
                src={data.ImageUrl}
                alt="Campaign preview"
                width={250}
                height={150}
                style={{
                  objectFit: "contain",
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                }}
                preview={false}
              />
            )}

            {data?.Channel === ChannelEnum.Email ? (
              data?.Messages?.[0]?.Content ? (
                <iframe
                  key={data.Messages[0].Content}
                  srcDoc={data.Messages[0].Content}
                  title="Email Preview"
                  style={{
                    width: "calc(100% + 24px)",
                    margin: "-12px",
                    border: "none",
                    display: "block",
                    minHeight: 400,
                  }}
                  onLoad={(e) => {
                    try {
                      const h =
                        e.currentTarget.contentDocument?.body?.scrollHeight;
                      if (h) e.currentTarget.style.height = `${h}px`;
                    } catch {}
                  }}
                />
              ) : (
                <span style={{ color: "#888" }}>No preview available.</span>
              )
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: data?.Messages?.[0]?.Content
                    ? DOMPurify.sanitize(
                        formatContent(
                          data.Messages[0].Content,
                          data?.Channel ?? ChannelEnum.Sms,
                        ),
                      )
                    : "Your Message will appear here...",
                }}
              />
            )}

            {selectedMessageTemplates && (
              <Space
                direction="vertical"
                style={{ width: "100%", marginTop: 16 }}>
                {selectedMessageTemplates?.TextLink1 && (
                  <Button
                    block
                    style={{ color: "#00A884", fontWeight: 600 }}
                    icon={
                      <img
                        src="/icon/icon-button-whatsapp.svg"
                        alt=""
                        style={{ width: 16 }}
                      />
                    }
                    onClick={() => {
                      const url = data?.Messages?.[0]?.Link;
                      if (url) window.open(url, "_blank");
                    }}>
                    {selectedMessageTemplates.TextLink1}
                  </Button>
                )}

                {selectedMessageTemplates?.TextLink2 && (
                  <Button
                    block
                    style={{ color: "#00A884", fontWeight: 600 }}
                    icon={
                      <img
                        src="/icon/icon-button-whatsapp.svg"
                        alt=""
                        style={{ width: 16 }}
                      />
                    }>
                    {selectedMessageTemplates.TextLink2}
                  </Button>
                )}
              </Space>
            )}
          </>
        ),
      },
    ];
  }, [
    data,
    selectedVoucher,
    selectedEVouchers,
    selectedMessageTemplates,
    previewFieldLabel,
    loadingEVoucher,
    eVoucherNotFoundCount,
    evoucherListExpanded,
  ]);

  // const messageRows: Row[] = [
  //   ...(data?.Type === CampaignTypeEnum.Voucher ||
  //   data?.Type === CampaignTypeEnum.VoucherPoint
  //     ? [
  //         {
  //           key: "reward",
  //           field: "Reward Included",
  //           value:
  //             data?.Type === CampaignTypeEnum.Voucher ||
  //             data?.Type === CampaignTypeEnum.VoucherPoint
  //               ? selectedVoucher?.Name + " | " + selectedVoucher?.DiscountLabel
  //               : (data?.SKU ?? "-"),
  //         },
  //       ]
  //     : []),
  //   ...(data?.Type === CampaignTypeEnum.EVoucher
  //     ? [
  //         {
  //           key: "reward",
  //           field: "Reward Included",
  //           value: selectedEVoucher?.Name + " a| $" + selectedEVoucher?.Price,
  //         },
  //       ]
  //     : []),
  //   ...(data?.Channel === ChannelEnum.WhatsAppPremium
  //     ? [
  //         {
  //           key: "message_template",
  //           field: "Message Template",
  //           value: selectedMessageTemplates?.Name,
  //         },
  //       ]
  //     : []),
  //   {
  //     key: "preview",
  //     field: `${previewFieldLabel}`,
  //     value: (
  //       <>
  //         {data?.ImageUrl && (
  //           <AntdImage
  //             src={data?.ImageUrl}
  //             alt="Campaign preview"
  //             width={250}
  //             height={150}
  //             style={{
  //               objectFit: "contain",
  //               borderRadius: 8,
  //               border: "1px solid #f0f0f0",
  //             }}
  //             preview={false}
  //           />
  //         )}
  //         <div
  //           dangerouslySetInnerHTML={{
  //             __html: data?.Messages[0]?.Content
  //               ? DOMPurify.sanitize(
  //                   formatContent(
  //                     data.Messages[0].Content,
  //                     data?.Channel ?? ChannelEnum.Sms,
  //                   ),
  //                 )
  //               : "Your Message will appear here...",
  //           }}
  //         ></div>
  //         {selectedMessageTemplates && (
  //           <Space
  //             direction="vertical"
  //             style={{ width: "100%", marginTop: 16 }}
  //           >
  //             {selectedMessageTemplates?.TextLink1 && (
  //               <Button
  //                 block
  //                 style={{
  //                   color: "#00A884",
  //                   fontWeight: 600,
  //                 }}
  //                 icon={
  //                   <img
  //                     src="/icon/icon-button-whatsapp.svg"
  //                     style={{ width: 16 }}
  //                   />
  //                 }
  //                 onClick={() => {
  //                   const url = data?.Messages?.[0]?.Link;
  //                   if (url) {
  //                     window.open(url, "_blank");
  //                   }
  //                 }}
  //               >
  //                 {selectedMessageTemplates?.TextLink1}
  //               </Button>
  //             )}

  //             {selectedMessageTemplates?.TextLink2 && (
  //               <Button
  //                 block
  //                 style={{
  //                   color: "#00A884",
  //                   fontWeight: 600,
  //                 }}
  //                 icon={
  //                   <img
  //                     src="/icon/icon-button-whatsapp.svg"
  //                     style={{ width: 16 }}
  //                   />
  //                 }
  //               >
  //                 {selectedMessageTemplates?.TextLink2}
  //               </Button>
  //             )}
  //           </Space>
  //         )}
  //         {/* {data?.Messages[0]?.Link} */}
  //       </>
  //     ),
  //   },
  // ];

  const handleEdit = () => {
    navigateWithQuery(RoutePaths.IFRAME_UPDATE_CAMPAIGN, "id", data?.Id);
  };

  const fetchCampaignData = async () => {
    if (id) {
      try {
        setLoading(true);
        const data = await campaignService.getCampaignDetailsById(Number(id));

        if (data) {
          setData(data?.Campaign);
          if (
            Array.isArray(data?.Campaign?.ExcludeMembers) &&
            data.Campaign.ExcludeMembers.length
          ) {
            setExcludeRows(
              data.Campaign.ExcludeMembers.map((m: any) => ({
                "Customer Contact": m.CustomerContact ?? m.PhoneNumber ?? "",
                "Email Address": m.Email ?? "",
              })),
            );
          }
          if (
            Array.isArray(data?.Campaign?.TargetMembers) &&
            data.Campaign.TargetMembers.length
          ) {
            setTargetRows(
              data.Campaign.TargetMembers.map((m: any) => ({
                "Customer Contact": m.CustomerContact ?? m.PhoneNumber ?? "",
                "Email Address": m.Email ?? "",
                Status: m.Found ? TARGET_MEMBER_FOUND : TARGET_MEMBER_NOT_FOUND,
              })),
            );
          }
          if (data?.AllRunLogPerformances) {
            setAllRunLogPerformances(data.AllRunLogPerformances);
          }

          switch (data?.Campaign?.Channel) {
            case ChannelEnum.Sms:
              setPrice(Number(process.env.REACT_APP_SMS_COST));
              break;

            case ChannelEnum.WhatsAppPremium:
              setPrice(Number(process.env.REACT_APP_WHATSAPP_COST));
              break;

            case ChannelEnum.WhatsAppStandard:
              setPrice(Number(process.env.REACT_APP_WHATSAPP_COST));
              break;

            default:
              setPrice(0);
          }

          if (data?.Voucher) {
            setSelectedVoucher(data?.Voucher);
          }
          if (data?.Campaign.Messages?.[0].CampaignTemplates) {
            setSelectedMessageTemplates(
              data?.Campaign.Messages?.[0].CampaignTemplates,
            );
          }

          if (data?.CampaignPerformance) {
            setCardData((prev) =>
              prev.map((card) => {
                if (card.key === "card-data-1")
                  return {
                    ...card,
                    value: formatCurrency(
                      data?.CampaignPerformance?.RevenueRewards ?? 0,
                    ),
                    order: data?.CampaignPerformance?.TotalOrderRewards ?? 0,
                  };
                if (card.key === "card-data-2")
                  return {
                    ...card,
                    value: formatCurrency(
                      data?.CampaignPerformance?.RevenueCampaign ?? 0,
                    ),
                    order: data?.CampaignPerformance?.TotalOrderCampaign ?? 0,
                  };
                if (card.key === "card-data-3")
                  return {
                    ...card,
                    value: `${
                      data?.CampaignPerformance?.ROI.toLocaleString("en-US") ??
                      0
                    }%`,
                  };
                if (card.key === "card-data-4")
                  return {
                    ...card,
                    value: `${data?.CampaignPerformance?.CTR.toLocaleString("en-US") ?? 0}%`,
                    order: data?.CampaignPerformance?.Clicks ?? 0,
                  };
                return card;
              }),
            );
          } else {
            const runLogs = (data?.Campaign?.CampaignRunLog ?? []).filter(
              (log: any) => log.Status === true,
            );
            if (runLogs.length > 0) {
              const totalClicks = runLogs.reduce((sum: number, log: any) => {
                const logClicks =
                  log.Clicks ??
                  (log.MemberLogs ?? []).reduce(
                    (s: number, m: any) => s + (m.Clicks || 0),
                    0,
                  );
                return sum + logClicks;
              }, 0);
              const totalDelivered = runLogs.reduce(
                (sum: number, log: any) => sum + (log.SuccessCount || 0),
                0,
              );
              const ctr =
                totalDelivered > 0
                  ? ((totalClicks / totalDelivered) * 100).toFixed(2)
                  : "0.00";
              setCardData((prev) =>
                prev.map((card) => {
                  if (card.key === "card-data-4")
                    return { ...card, value: `${ctr}%`, order: totalClicks };
                  return card;
                }),
              );
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchEVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingEVoucher(true);
        let email = campaignInfo?.Email ?? "";
        const accounts = await billingService.getCreditCard();
        if (accounts && accounts.length > 0) {
          email = accounts.map((a) => a.email).join(",");
        }
        const dataEVoucher = await campaignService.getAllEVouchersV2(email);
        if (dataEVoucher) {
          const mapped = mapToEVouchers(dataEVoucher);
          setListEVoucher(mapped);
        }
      } catch (error) {
        console.error("Error fetching E-Voucher:", error);
      } finally {
        setLoadingEVoucher(false);
      }
    }
  };

  const fetchCampaignInfo = async () => {
    if (brandId) {
      try {
        const data = await campaignService.getCampaignInfo(brandId);
        if (data) {
          setCampaignInfo(data);
        }
      } catch (error) {
        console.error("Error fetching campaign info:", error);
      }
    }
  };

  useEffect(() => {
    setRunLogPage(1);
  }, [selectedRunLogId]);

  useEffect(() => {
    fetchCampaignData();
    fetchCampaignInfo();
    // fetchCampaignData/fetchCampaignInfo are intentionally omitted — they're
    // redefined every render, and including them would re-fetch on every
    // render instead of only when the campaign id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (campaignInfo?.Email) {
      fetchEVoucherList();
    }
    // fetchEVoucherList is intentionally omitted — see note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignInfo]);

  useEffect(() => {
    const skus = getCampaignEVoucherSkus(data);
    if (!skus.length || !listEVoucher?.length) return;
    const skuSet = new Set(skus);
    const matches = listEVoucher.filter((v) => skuSet.has(v.SKU));
    if (matches.length) {
      setSelectedEVouchers(matches);
    }
  }, [listEVoucher, data]);

  const handleConfirm = useCallback(async () => {
    if (!variant) return;
    setLoading(true);
    if (variant === "activate") {
      await campaignService.updateCampaignDetailsStatus(
        data?.Id ?? 0,
        StatusEnum.Active,
        false,
      );
      setIsModalSuccessActivateCampaignVisible(true);
    }
    if (variant === "delete") {
      const result = await campaignService.deleteCampaignDetails(data?.Id ?? 0);

      if (result === 1) {
        setActionType("delete");
        setIsModalSuccessDeleteCampaignVisible(true);
      }
    }
    if (variant === "cancel") {
      const result = await campaignService.updateCampaignDetailsStatus(
        data?.Id ?? 0,
        StatusEnum.Cancelled,
      );
      if (result === 1) {
        setActionType("cancel");
        setIsModalSuccessDeleteCampaignVisible(true);
        // fetchHistoryCampaign();
      }
    }
    setVariant(null);
    setLoading(false);
  }, [variant, data?.Id]);

  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          <Col>
            Detail {data?.Channel ? ChannelKeyMap[data?.Channel] : ""} Campaign
          </Col>
          <Col>
            <Button
              color="primary"
              variant="outlined"
              onClick={() =>
                navigateWithQuery(
                  RoutePaths.IFRAME_TARGET_MARKETING_HOME,
                  "id",
                  null,
                )
              }>
              Back
            </Button>
          </Col>
        </Row>
      }>
      {(data?.Status === StatusEnum.Completed ||
        isRunning ||
        (data?.ScheduleType !== TypeEnum.Onetime &&
          allStartedRunLogs.length > 0)) && (
        <Card
          title={"Campaign Performance"}
          className="card-campaign-performance"
          style={{ marginBottom: 15 }}
          loading={loading}>
          {data?.ScheduleType !== TypeEnum.Onetime &&
            allStartedRunLogs.length > 0 && (
              <>
                <Space wrap style={{ marginBottom: 8 }}>
                  {[
                    {
                      label: "Summary (All Runs)",
                      value: "all",
                      disabled: false,
                    },
                    ...allStartedRunLogs.map((log, idx) => ({
                      label: `Run #${idx + 1}`,
                      value: String(log.Id),
                      disabled: log.StartDate
                        ? new Date(log.StartDate) > new Date()
                        : false,
                    })),
                  ].map((opt) => (
                    <Button
                      key={opt.value}
                      type={
                        selectedRunLogId === opt.value ? "primary" : "default"
                      }
                      variant={
                        selectedRunLogId === opt.value ? "solid" : "outlined"
                      }
                      disabled={opt.disabled}
                      onClick={() => setSelectedRunLogId(opt.value)}>
                      {opt.label}
                    </Button>
                  ))}
                </Space>
                {selectedRunLogId === "all" ? (
                  <Alert
                    showIcon
                    style={{
                      width: "100%",
                      marginBottom: 16,
                      textAlign: "left",
                    }}
                    message={
                      <span style={{ color: "#1565C0" }}>
                        This summary shows the overall performance of all
                        completed runs.
                      </span>
                    }
                    type="info"
                  />
                ) : (
                  <div style={{ marginBottom: 16 }} />
                )}
              </>
            )}
          <Row gutter={[12, 12]}>
            {displayCardData?.map((item, index) => {
              return (
                <Col
                  xs={24}
                  md={12}
                  xl={6}
                  key={index}
                  style={{ width: isMobile ? "100%" : "unset" }}
                  // style={{
                  //   marginBottom: isMobileOrTablet && !isLast ? 20 : 0,
                  // }}
                >
                  <Card className="card-member">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        height: 70,
                      }}>
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            lineHeight: "1.2",
                          }}>
                          {item.title}
                          {item.tooltip && (
                            <Tooltip title={item.tooltip}>
                              <InfoCircleOutlined
                                style={{
                                  fontSize: 14,
                                  marginLeft: 6,
                                  color: "#888",
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 700 }}>
                          {item.value}
                        </div>
                        {item.key === "card-data-1" ||
                        item.key === "card-data-2" ||
                        item.key === "card-data-4" ? (
                          <Alert
                            className="alert-show-count"
                            description={
                              <div>
                                <span style={{ color: "#1565C0" }}>
                                  {item.order} {item.countLabel ?? "Order"}
                                </span>
                              </div>
                            }
                            type="info"
                          />
                        ) : (
                          <div style={{ height: 25 }}></div>
                        )}
                      </div>
                      <img src={item.icon} alt="icon" width="50" height="50" />
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      )}

      <Table<OverviewRow>
        className="table-no-header"
        columns={columns}
        dataSource={overviewRows}
        bordered
        size="small"
        title={() => "Campaign Overview"}
        pagination={false}
        rowKey="key"
      />

      <Table<OverviewRow>
        className="table-no-header"
        columns={columns}
        dataSource={scheduleRows}
        bordered
        size="small"
        style={{ marginTop: 10 }}
        title={() => "Schedule Time"}
        pagination={false}
        rowKey="key"
      />

      <Table<OverviewRow>
        className="table-no-header"
        columns={columns}
        dataSource={messageRows}
        bordered
        size="small"
        style={{ marginTop: 10 }}
        title={() => "Message Content"}
        pagination={false}
        rowKey="key"
      />
      {(data?.Status === StatusEnum.Completed || isRunning) &&
        data?.ScheduleType === TypeEnum.Onetime && (
          <CampaignMemberLogTable
            data={data?.CampaignRunLog?.[0]?.MemberLogs ?? []}
            campaign={data}
            runLogId={data?.CampaignRunLog?.[0]?.Id}
          />
        )}
      {data?.ScheduleType !== TypeEnum.Onetime &&
        allStartedRunLogs.length > 0 &&
        (() => {
          if (selectedRunLogId !== "all") {
            const runLog = allStartedRunLogs.find(
              (log) => String(log.Id) === selectedRunLogId,
            );
            const idx = allStartedRunLogs.findIndex(
              (log) => String(log.Id) === selectedRunLogId,
            );
            if (!runLog) return null;
            return (
              <CampaignMemberLogTable
                key={selectedRunLogId}
                data={runLog.MemberLogs ?? []}
                campaign={data ?? undefined}
                title={`Run #${idx + 1}`}
                runLogId={runLog.Id}
              />
            );
          }

          const summaryColumns: ColumnsType<any> = [
            {
              title: "Run",
              dataIndex: "runNumber",
              key: "run",
              render: (v: number) => `Run #${v}`,
              width: 80,
            },
            {
              title: "Send Date",
              dataIndex: "StartDate",
              key: "startDate",
              render: (v: Date) =>
                v ? dayjs(v).add(-7, "hour").format("YYYY-MM-DD HH:mm") : "-",
            },
            {
              title: "Status",
              dataIndex: "Status",
              key: "status",
              render: (v: boolean | null, row: any) => {
                if (v === true)
                  return (
                    <Tag color="green" style={{ color: "#389e0d" }}>
                      Success
                    </Tag>
                  );
                if (v === false)
                  return (
                    <Tag color="red" style={{ color: "#cf1322" }}>
                      Failed
                    </Tag>
                  );
                const isPast = row.StartDate
                  ? new Date(row.StartDate) <= new Date()
                  : false;
                if (isPast)
                  return (
                    <Tag color="blue" style={{ color: "#1677ff" }}>
                      Running
                    </Tag>
                  );
                return (
                  <Tag color="yellow" style={{ color: "#d48806" }}>
                    Scheduled
                  </Tag>
                );
              },
            },
            // {
            //   title: "Total Members",
            //   dataIndex: "TotalMembers",
            //   key: "total",
            //   render: (v: number) => v ?? "-",
            // },
            {
              title: "Sent Count",
              dataIndex: "SuccessCount",
              key: "success",
              render: (v: number | null) => (v != null ? v : "-"),
            },
            {
              title: "Failed Count",
              dataIndex: "FailCount",
              key: "fail",
              render: (v: number | null) => (v != null ? v : "-"),
            },
            {
              title: "Actions",
              key: "actions",
              render: (_: any, row: any) => {
                if (row.Status !== true && row.Status !== null) return null;
                const isFuture = row.StartDate
                  ? new Date(row.StartDate) > new Date()
                  : false;
                return (
                  <Space>
                    <Button
                      size="middle"
                      variant="outlined"
                      color="default"
                      disabled={isFuture}
                      onClick={() => setSelectedRunLogId(String(row.Id))}>
                      View
                    </Button>
                    <Button
                      size="middle"
                      type="primary"
                      loading={downloadingRunLogId === row.Id}
                      disabled={isFuture}
                      onClick={() => handleDownloadDeliveryLog(row.Id)}>
                      Download
                    </Button>
                  </Space>
                );
              },
            },
          ];

          const allRunLogs = (data?.CampaignRunLog ?? [])
            .slice()
            .sort(
              (a, b) =>
                new Date(a.StartDate ?? 0).getTime() -
                new Date(b.StartDate ?? 0).getTime(),
            )
            .map((log, idx) => ({ ...log, key: log.Id, runNumber: idx + 1 }));

          const runLogStartIdx = (runLogPage - 1) * runLogPageSize;
          const paginatedRunLogs = allRunLogs.slice(
            runLogStartIdx,
            runLogStartIdx + runLogPageSize,
          );

          return (
            <Card
              title="Delivery Logs (Recurring Runs)"
              className="card-recipient-detail"
              style={{ marginTop: 15 }}
              extra={
                <Row>
                  <Button
                    type="primary"
                    className="w-100"
                    loading={downloadingRunLogId === -1}
                    disabled={!hasActuallyStarted}
                    onClick={() => handleDownloadDeliveryLog()}>
                    <DownloadOutlined />
                    Download All Runs
                  </Button>
                </Row>
              }>
              <Table
                size="middle"
                columns={summaryColumns}
                dataSource={paginatedRunLogs}
                pagination={false}
                scroll={{ x: "max-content" }}
              />
              <ResponsivePagination
                totalItems={allRunLogs.length}
                isMobileOrTablet={window.innerWidth <= 1024}
                pageSizeOptions={[5, 10, 15]}
                onPageChange={(page: number, pageSize: number) => {
                  setRunLogPage(page);
                  setRunLogPageSize(pageSize);
                }}
              />
            </Card>
          );
        })()}
      {data?.Status === StatusEnum.Draft && (
        <Row
          gutter={[12, 12]}
          justify="space-between"
          style={{ marginTop: 15 }}>
          <Col xl={12} md={12} xs={24}>
            <Button
              danger
              onClick={() => setVariant("delete")}
              size="large"
              style={{ width: isMobile ? "100%" : "unset" }}>
              Delete Campaign
            </Button>
          </Col>
          {isMobile && (
            <Col xl={6} md={6} xs={24} style={{ textAlign: "right" }}>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleEdit}
                size="large"
                style={{ width: isMobile ? "100%" : "unset" }}>
                Edit Campaign
              </Button>
            </Col>
          )}

          {!isMobile ? (
            <Col
              xl={12}
              md={12}
              xs={24}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                flexWrap: "wrap",
                textAlign: "right",
              }}>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleEdit}
                size="large"
                style={{ width: isMobile ? "100%" : "unset" }}>
                Edit Campaign
              </Button>
              <Tooltip
                title={
                  isWithinOneHour ? (
                    <div className="custom-bubble-tooltip">
                      <strong>Activation is unavailable.</strong>
                      <div>
                        This campaign is locked as it gets closer to the
                        scheduled send time.
                      </div>
                    </div>
                  ) : (
                    ""
                  )
                }>
                <span>
                  <Button
                    type="primary"
                    onClick={() => {
                      onClickAction("activate");
                    }}
                    size="large"
                    disabled={isWithinOneHour}
                    style={{
                      pointerEvents: isWithinOneHour ? "none" : "auto",
                      width: isMobile ? "100%" : "unset",
                    }}>
                    Activate Campaign
                  </Button>
                </span>
              </Tooltip>
            </Col>
          ) : (
            <Col xl={6} md={6} xs={24} style={{ textAlign: "right" }}>
              <Tooltip
                title={
                  isWithinOneHour ? (
                    <div className="custom-bubble-tooltip">
                      <strong>Activation is unavailable.</strong>
                      <div>
                        This campaign is locked as it gets closer to the
                        scheduled send time.
                      </div>
                    </div>
                  ) : (
                    ""
                  )
                }>
                <span>
                  <Button
                    type="primary"
                    onClick={() => {
                      onClickAction("activate");
                    }}
                    size="large"
                    disabled={isWithinOneHour}
                    style={{
                      pointerEvents: isWithinOneHour ? "none" : "auto",
                      width: isMobile ? "100%" : "unset",
                    }}>
                    Activate Campaign
                  </Button>
                </span>
              </Tooltip>
            </Col>
          )}
        </Row>
      )}
      {data?.Status === StatusEnum.Active && (
        <Row
          justify="space-between"
          align="middle"
          style={{ marginTop: 15, flexWrap: "nowrap", gap: 12 }}>
          {/* Far left — Cancel */}
          <Col flex="none">
            <Tooltip
              title={
                isWithinOneHour ? (
                  <div className="custom-bubble-tooltip">
                    <strong>Cancellation is unavailable.</strong>
                    <div>
                      This campaign is locked as it gets closer to the scheduled
                      send time.
                    </div>
                  </div>
                ) : (
                  ""
                )
              }>
              <span>
                <Button
                  color="danger"
                  onClick={() => onClickAction("cancel")}
                  size="large"
                  disabled={isWithinOneHour || isBilled}
                  style={{
                    pointerEvents:
                      isWithinOneHour || isBilled ? "none" : "auto",
                  }}>
                  Cancel Campaign
                </Button>
              </span>
            </Tooltip>
          </Col>

          {/* Far right — Edit + Complete Payment */}
          <Col flex="none">
            <Space>
              <Tooltip
                title={
                  isWithinOneHour ? (
                    <div className="custom-bubble-tooltip">
                      <strong>Editing is unavailable.</strong>
                      <div>
                        This campaign is locked as it gets closer to the
                        scheduled send time.
                      </div>
                    </div>
                  ) : (
                    ""
                  )
                }>
                <span>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={handleEdit}
                    size="large"
                    disabled={isWithinOneHour || isBilled}
                    style={{
                      pointerEvents:
                        isWithinOneHour || isBilled ? "none" : "auto",
                    }}>
                    Edit Campaign
                  </Button>
                </span>
              </Tooltip>

              {/* {!isBilled && (
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    setBillingContext("pay");
                    setBillingModalOpen(true);
                  }}
                >
                  Complete Payment
                </Button>
              )} */}
            </Space>
          </Col>
        </Row>
      )}
      {data?.Status === StatusEnum.Cancelled && (
        <Row
          gutter={[12, 12]}
          justify="space-between"
          style={{ marginTop: 15 }}>
          <Col span={12}></Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Space>
              <Button
                size="large"
                color="danger"
                variant="outlined"
                onClick={() => setVariant("delete")}>
                Delete Campaign
              </Button>
            </Space>
          </Col>
        </Row>
      )}
      {variant && (
        <CampaignAlertModal
          open
          variant={variant}
          onCancel={() => setVariant(null)}
          onConfirm={handleConfirm}
          confirmLoading={loading}
        />
      )}
      <Modal
        open={isModalSuccessActivateCampaignVisible}
        onCancel={() => {
          setIsModalSuccessActivateCampaignVisible(false);
        }}
        footer={null}
        centered
        width={520}
        closable={true}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}>
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Campaign Active successfully
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your campaign is set to be sent on{" "}
            {dayjs(data?.CampaignStart).format("YYYY-MM-DD HH:mm")}. You can
            view or edit it from the campaign history
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              window.location.reload();
              // navigateWithQuery(RoutePaths.IFRAME_DETAIL_CAMPAIGN, "id", id);
            }}
            style={{ marginTop: 8 }}>
            View Details
          </Button>
        </Row>
      </Modal>
      <Modal
        open={isModalSuccessDeleteCampaignVisible}
        onCancel={() => {
          setIsModalSuccessDeleteCampaignVisible(false);
        }}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}>
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Campaign {actionType === "delete" ? "Deleted" : "Canceled"}
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            The Campaign has been{" "}
            {actionType === "delete"
              ? "permanently deleted"
              : "successfully canceled"}
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              navigateWithQuery(
                RoutePaths.IFRAME_TARGET_MARKETING_HOME,
                "id",
                null,
              );
              setIsModalSuccessDeleteCampaignVisible(false);
            }}
            style={{ marginTop: 8 }}>
            Ok
          </Button>
        </Row>
      </Modal>
      {/* <BillingModal
        open={billingModalOpen}
        campaignId={data?.Id ?? 0}
        campaignRunLogId={
          data?.CampaignRunLog?.reduce(
            (latest, current) => {
              if (!latest) return current;

              const currentTime = current.CreatedDate?.getTime() ?? -Infinity;
              const latestTime = latest.CreatedDate?.getTime() ?? -Infinity;

              return currentTime > latestTime ? current : latest;
            },
            undefined as (typeof data.CampaignRunLog)[number] | undefined,
          )?.Id ?? 0
        }
        campaignName={data?.Name ?? ""}
        channel={data?.Channel ?? null}
        estimatedMembers={data?.EstimatedTotalMembers ?? 0}
        costPerMessage={getChannelCost(data?.Channel ?? null)}
        maxBudget={data?.Budget}
        campaignStart={data?.CampaignStart}
        campaignEnd={data?.CampaignEnd}
        mode={data?.Mode}
        hidePayLater={billingContext === "pay"}
        smsSegments={
          data?.Channel === ChannelEnum.Sms
            ? getSmsSegments(
                data?.Messages?.[0]?.Content ?? "",
                isGsmText(data?.Messages?.[0]?.Content ?? ""),
              )
            : 1
        }
        onClose={() => setBillingModalOpen(false)}
        onConfirmPayment={
          billingContext === "activate"
            ? async (_email, referenceCode) => {
                try {
                  setLoading(true);
                  await campaignService.updateCampaignDetailsStatus(
                    data?.Id ?? 0,
                    StatusEnum.Active,
                    true,
                    referenceCode,
                  );
                } finally {
                  setLoading(false);
                }
              }
            : undefined
        }
        onPaymentLater={async () => {
          setBillingModalOpen(false);
          if (billingContext === "activate") {
            await campaignService.updateCampaignDetailsStatus(
              data?.Id ?? 0,
              StatusEnum.Active,
              false,
            );
            setIsModalSuccessActivateCampaignVisible(true);
          }
        }}
        onSuccess={() => {
          setBillingModalOpen(false);
          setPaymentSuccessModalOpen(true);
        }}
        onFailed={() => {
          setBillingModalOpen(false);
          setPaymentFailedModalOpen(true);
        }}
      /> */}
      <Modal
        open={paymentSuccessModalOpen}
        onCancel={() => setPaymentSuccessModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={true}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}>
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Payment Successful
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your payment has been processed successfully.
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              setPaymentSuccessModalOpen(false);
              window.location.reload();
            }}
            style={{ marginTop: 8 }}>
            View Details
          </Button>
        </Row>
      </Modal>
      <Modal
        open={paymentFailedModalOpen}
        onCancel={() => setPaymentFailedModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={true}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}>
          <CheckCircleFilled style={{ fontSize: 80, color: "#e74c3c" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Payment Failed
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            We were unable to process your payment. You can retry later.
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <Button
              size="large"
              className="w-100"
              onClick={async () => {
                setPaymentFailedModalOpen(false);
                setBillingModalOpen(false);
                if (billingContext === "activate") {
                  setLoading(true);
                  try {
                    await campaignService.updateCampaignDetailsStatus(
                      data?.Id ?? 0,
                      StatusEnum.Active,
                      false,
                    );
                    setIsModalSuccessActivateCampaignVisible(true);
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              style={{ marginTop: 8 }}>
              Set Up Payment Later
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              size="large"
              className="w-100"
              onClick={() => {
                setPaymentFailedModalOpen(false);
                setBillingModalOpen(true);
              }}
              style={{ marginTop: 8 }}>
              Retry Payment
            </Button>
          </Col>
        </Row>
      </Modal>
      <RFMGeneratingModal
        visible={loading}
        message="Preparing Campaign Data..."
      />
      <CustomModal
        type="timeout"
        title={titleTimeoutModal}
        message={messageTimeoutModal}
        isVisible={showTimeout}
        onClose={() => setShowTimeout(false)}
        autoClose={false}
      />
      <PreviewMemberModal
        open={isPreviewOpen}
        onClose={closePreview}
        estimatedMember={data?.CampaignRunLog?.[0]?.MemberLogs ?? []}
        isDetailPage={true}
        messageContent={data?.Messages?.[0]?.Content}
        campaignType={data?.Type}
      />
      <ExcludeMemberPreviewModal
        open={isExcludePreviewOpen}
        onClose={() => setIsExcludePreviewOpen(false)}
        rows={excludeRows}
      />
      <TargetMemberPreviewModal
        open={isTargetPreviewOpen}
        onClose={() => setIsTargetPreviewOpen(false)}
        rows={targetRows}
      />
    </Card>
  );
};

export default DetailCampaign;
